import { OrderAdjustmentType, OrderRefundApprovalStatus, OrderStatus } from '@shared/api/graphql/gql/graphql';
import { Temporal, clock, parseInstant } from '@shared/lib/date-time';

import type { OrderListEntity } from '../model/types';

/** Баллы лояльности, списанные на строку заказа (из loyalty-adjustment). */
export const getOrderLineLoyaltyPoints = (order: OrderListEntity, orderLineId: string): number => {
  const loyaltyAdjustment = order.adjustments.find(
    (adjustment) => adjustment.adjustment_type === OrderAdjustmentType.LoyaltyDiscount,
  );

  return loyaltyAdjustment?.line_points.find((line) => line.order_line_id === orderLineId)?.points ?? 0;
};

/**
 * Строки с net_amount = 0 возвращаемы, только если они полностью списаны
 * баллами: сервер для них восстанавливает баллы без денежных allocations.
 */
export const getRefundableOrderLines = (order: OrderListEntity): OrderListEntity['lines'] =>
  order.lines.filter(
    (line) =>
      line.refunded_amount === 0 &&
      (line.net_amount > 0 || (line.net_amount === 0 && getOrderLineLoyaltyPoints(order, line.id) > 0)),
  );

/**
 * Единый предикат «возврат в принципе возможен» для всех экранов, откуда
 * открывается панель возврата: страница заказа и виджет чека шахматки.
 * Право `payments.refund` проверяет вызывающий — оно живёт в auth-store.
 */
export const canRefundOrder = (order: OrderListEntity | null): boolean =>
  order !== null &&
  [OrderStatus.Paid, OrderStatus.PartiallyRefunded].includes(order.status) &&
  getRefundableOrderLines(order).length > 0;

/**
 * Единый предикат «одобрение возврата действует прямо сейчас»: выдано руководителем
 * и ещё не сгорело. Эталон на бэке — OrderRefundApproval::grantsRefund(); экран
 * кассира и экран согласований обязаны решать это одинаково.
 */
export const isRefundApprovalLive = (
  status: OrderRefundApprovalStatus | null | undefined,
  expiresAt: string | null | undefined,
  now: Temporal.Instant = clock.now(),
): boolean => {
  if (status !== OrderRefundApprovalStatus.Approved) return false;
  if (expiresAt == null) return true;
  const expires = parseInstant(expiresAt);
  return expires !== null && Temporal.Instant.compare(expires, now) > 0;
};
